import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Quote } from 'lucide-react';
import AnimatedText from './AnimatedText';

const testimonials = [
  {
    id: 1,
    name: 'Sarah Mitchell',
    role: 'CEO · TechVenture',
    content:
      "Working with HypeLab transformed our online presence completely. Rutvi's strategic approach and creative vision helped us achieve a 400% increase in social engagement within just 3 months.",
    accent: false,
  },
  {
    id: 2,
    name: 'James Chen',
    role: 'Marketing Director · Luxe Brands',
    content:
      'The UGC content strategy HypeLab developed for us was exceptional. Authentic, engaging, and perfectly aligned with our brand voice. Our conversion rates have never been higher.',
    accent: true,
  },
  {
    id: 3,
    name: 'Emily Watson',
    role: 'Founder · Wellness Studio',
    content:
      "Rutvi truly understands what it takes to build a brand in today's digital landscape. Her expertise in both content creation and marketing strategy is unmatched.",
    accent: false,
  },
  {
    id: 4,
    name: 'Michael Roberts',
    role: 'Co-founder · StartUp Hub',
    content:
      'From website development to social media management, HypeLab handled everything flawlessly. Our startup went from unknown to industry-recognized in under a year.',
    accent: true,
  },
];

const StackCard = ({ t, index, total }) => {
  const cardRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: cardRef,
    offset: ['start end', 'end start'],
  });
  // Cards behind shrink and fade slightly as new ones stack on top
  const scale = useTransform(scrollYProgress, [0, 0.5, 1], [0.92, 1, 0.94]);
  const opacity = useTransform(scrollYProgress, [0, 0.4, 1], [0.4, 1, 0.6]);
  const dark = t.accent;
  const stickyTop = 100 + index * 24; // each card pins slightly lower for the "stack" effect

  return (
    <motion.article
      ref={cardRef}
      data-testid={`testimonial-card-${t.id}`}
      style={{
        scale,
        opacity,
        top: `${stickyTop}px`,
        zIndex: index + 1,
      }}
      className={`sticky w-full rounded-[2.5rem] p-8 sm:p-14 lg:p-20 border shadow-lg ${
        dark
          ? 'bg-[#1C1917] text-[#F4F4F3] border-[#1C1917]'
          : 'bg-white text-[#1C1917] border-[#1C1917]/10'
      }`}
    >
      <div className="flex items-start justify-between gap-6 mb-10">
        <Quote
          className={`w-14 h-14 sm:w-20 sm:h-20 ${dark ? 'text-[#AA94FF]' : 'text-[#AA94FF]'}`}
          strokeWidth={1.5}
        />
        <span
          className={`text-sm font-bold tracking-[0.15em] uppercase ${
            dark ? 'text-[#F4F4F3]/50' : 'text-[#78716C]'
          }`}
        >
          {String(index + 1).padStart(2, '0')} / {String(total).padStart(2, '0')}
        </span>
      </div>

      <p
        className={`text-2xl sm:text-3xl lg:text-4xl font-semibold leading-[1.3] tracking-[-0.01em] mb-12 ${
          dark ? 'text-[#F4F4F3]' : 'text-[#1C1917]'
        }`}
        style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
      >
        “{t.content}”
      </p>

      <div className={`flex items-center justify-between pt-8 border-t ${dark ? 'border-[#F4F4F3]/15' : 'border-[#1C1917]/10'}`}>
        <div>
          <p
            className={`text-lg font-bold ${dark ? 'text-[#F4F4F3]' : 'text-[#1C1917]'}`}
            style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
          >
            {t.name}
          </p>
          <p className={`text-sm ${dark ? 'text-[#F4F4F3]/60' : 'text-[#78716C]'}`}>
            {t.role}
          </p>
        </div>
        <div className="w-16 h-16 rounded-full bg-[#AA94FF] flex items-center justify-center">
          <span
            className="text-2xl font-black text-[#F4F4F3]"
            style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
          >
            {t.name[0]}
          </span>
        </div>
      </div>
    </motion.article>
  );
};

const Testimonials = () => {
  return (
    <section
      id="testimonials"
      data-testid="testimonials-section"
      className="py-28 sm:py-40 px-6 sm:px-8 lg:px-12 bg-[#F4F4F3]"
    >
      <div className="max-w-5xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-16">
          <p className="lg:col-span-4 text-xs sm:text-sm font-bold uppercase tracking-[0.2em] text-[#AA94FF]">
            Testimonials
          </p>
          <div className="lg:col-span-8">
            <AnimatedText
              as="h2"
              className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-[-0.03em] leading-[0.98] text-[#1C1917]"
              style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
              y={50}
            >
              Kind words from brands we've grown.
            </AnimatedText>
          </div>
        </div>

        {/* Stack container — each card is sticky at different offsets */}
        <div className="relative flex flex-col gap-6" style={{ minHeight: `${testimonials.length * 60}vh` }}>
          {testimonials.map((t, i) => (
            <StackCard key={t.id} t={t} index={i} total={testimonials.length} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
